import { AdBiddingProvider, IAdBiddingProviderConfig } from '../../../'
import { IConfiguration } from '../../../../../../domain/conf'
import { IdentityEvent } from '../../../../../../services/common/events/identity'
import { Errors } from '../../../../../../utils/common'
import { logger } from '../../../../../../utils/common/logger'
import { DOMUtils } from '../../../../../../utils/dom'
import { IAd } from '../../../../ad'
import { AdBiddingBase } from '../base'

type UserStatus = 'anonymous' | 'subscribed' | 'registered'

interface IUserStateInfo {
    loggedIn: string
    status: UserStatus
}

interface IUserProfile {
    email?: string
    adLight?: boolean
}

interface IWindow {
    googletag: any
    location: any
    optiCommon: any
    pbjs: any  // Added for Prebid.js integration
    setTimeout: any
}

declare let window: IWindow

/**
 * Optimera Ad Bidding Provider Implementation
 *
 * This class manages the integration with Optimera ad services for targeted advertising
 * using Prebid.js with OptimeraRTD module. It handles loading of the Optimera Common Library
 * and configuring the RTD module.
 */
class Optimera extends AdBiddingBase {
    private static instance: Optimera

    public requestAds: undefined
    public optimeraReady = false
    public optimeraProviderName = 'optimeraRTD'
    public pageHost: string
    public pagePath: string
    public dataHost: string
    public URLPath: string

    protected clientId: string | undefined
    protected failSafeTimeout = 3500
    protected waitForBidder = true

    // Keep track of loaded scripts to prevent duplicate loading
    private loadedScripts: Set<string> = new Set()

    constructor() {
        super()
    }

    /**
     * Get Singleton instance of Optimera
     * @returns {Optimera} The singleton instance
     */
    public static getInstance() {
        if (!Optimera.instance) {
            Optimera.instance = new Optimera()
        }
        return Optimera.instance
    }

    /**
     * Handler for optiCommon.js loading error
     */
    public onOptiCommonError() {
        logger('error', 'Optimera Common Library loading error')
        document.dispatchEvent(new Event(Errors.OPTIMERA_ERROR))
    }

    /**
     * Initialize Optimera Common Library configuration
     * @param {IConfiguration} conf - The application configuration
     */
    public initializeOptiCommon(conf: IConfiguration): void {
        // Find Optimera client ID from configuration
        const optimeraConfig = conf.ads.bidding.find((item) =>
            item.client === AdBiddingProvider.OPTIMERA
        )
        this.clientId = optimeraConfig?.account

        if (!this.clientId) {
            logger('error', 'Optimera client ID is missing')
            return
        }

        const femUrl = conf.analytics?.tags?.femUrl || ''
        this.URLPath = femUrl ? `${femUrl}optiCommon-1.2.1.js` : ''
        try {
            this.pageHost = window.location.host
            this.pagePath = window.location.pathname
        } catch (error) {
            logger('error', 'Error accessing window.location', error)
            this.pageHost = ''
            this.pagePath = ''
        }
        this.dataHost = 'https://dyv1bugovvq1g.cloudfront.net'
    }

    /**
     * Initializes Optimera Common Library script
     * Must be called in the HEAD of the page, above any ad code
     */
    public loadOptiCommon(): void {
        if (!this.URLPath) {
            logger('error', 'OptiCommon URL path is not defined')
            return
        }
        // Prevent duplicate loading
        if (this.loadedScripts.has(this.URLPath)) {
            logger('debug', 'Optimera Common Library already loaded')
            return
        }

        logger('debug', 'Loading Optimera Common Library', { path: this.URLPath })

        DOMUtils.loadScript(
            this.URLPath,
            undefined,
            this.onOptiCommonError,
            () => {
                logger('debug', 'Optimera Common Library loaded successfully')
                this.loadedScripts.add(this.URLPath)

                // Initialize optiCommon library after it's loaded
                if (window.googletag?.cmd) {
                    window.googletag.cmd.push(() => {
                        this.optimeraReady = true
                        this.initializeOptiCommonLib()
                        this.configureOptimeraRTD()
                    })
                } else {
                    logger('error', 'googletag.cmd is not available')
                }
            }
        )
    }

    /**
     * Initialize bidding functionality with provided configurations
     * @param {IAdBiddingProviderConfig[]} confs - Array of bidding configurations
     */
    public initializeBidding(confs: IAdBiddingProviderConfig[]): void {
        const conf = confs.find((config) => config.client === AdBiddingProvider.OPTIMERA)

        if (conf) {
            logger('debug', 'Initializing Optimera bidding', { conf })
            this.loadOptiCommon()
        } else {
            logger('debug', 'No Optimera configuration found, skipping initialization')
        }
    }

    /**
     * Fetch Optimera data and apply ad targeting
     * prebid will handle this
     *
     * @param {IAd[]} [ads] - Optional array of ad objects
     * @returns {Promise<any>} Promise that resolves when bidding is complete
     */
    public async requestAdsPromise(ads?: IAd[]): Promise<any> {
        // Return immediately if Optimera RTD is not ready
        if (!this.optimeraReady || !window.optiCommon) {
            logger('warn', 'Optimera RTD is not ready, skipping bid request')
            return Promise.resolve({ success: false, message: 'Optimera RTD not ready' })
        }

        logger('debug', 'Optimera requestAdsPromise called', { adsCount: ads?.length })
    }

    /**
     * Get user state information from local storage
     *
     * @returns {IUserStateInfo} Object with user status and login state
     */
    public getUserStateInfo(): IUserStateInfo {
        try {
            const storedData = localStorage.getItem('x-id')

            if (!storedData) {
                logger('debug', 'No user data found in localStorage')
                return {
                    loggedIn: 'false',
                    status: 'anonymous'
                }
            }

            const parsedData = JSON.parse(storedData)
            const profile: IUserProfile = parsedData.data || {}

            const isAnonymous = !(profile && !!profile.email)
            const isSubscribed = !!(profile && profile.adLight)

            let status: UserStatus
            const loggedIn = !isAnonymous ? 'true' : 'false'

            if (isAnonymous) {
                status = 'anonymous'
            } else if (isSubscribed) {
                status = 'subscribed'
            } else {
                status = 'registered'
            }

            logger('debug', 'Retrieved user state', { status, loggedIn })
            return { status, loggedIn }
        } catch (error) {
            console.error('Error parsing user profile:', error)
            return {
                loggedIn: 'false',
                status: 'anonymous'
            }
        }
    }

    /**
     * Configure OptimeraRTD module for Prebid
     * Sets up the Optimera RTD provider with the required parameters
     */
    private configureOptimeraRTD(): void {
        if (!window.pbjs || !this.clientId || !window.optiCommon) {
            logger('error', 'Cannot configure OptimeraRTD: missing dependencies')
            return
        }

        try {
            logger('debug', 'Configuring OptimeraRTD module')
            const currentConfig = window.pbjs.getConfig ? window.pbjs.getConfig() : {}
            const currentProviders = currentConfig.realTimeData &&
                                    currentConfig.realTimeData.dataProviders || []

            // Check if OptimeraRTD provider already exists
            const existingOptimeraIndex = currentProviders.findIndex(
                (provider: any) => provider.name === this.optimeraProviderName
            )

            // Prepare the optimera provider configuration with all required parameters
            const optimeraProviderConfig = {
                name: this.optimeraProviderName,
                params: {
                    clientID: this.clientId,
                    device: window.optiCommon.getSplitterString(),
                    optimeraKeyName: 'optimera',
                },
                waitForIt: this.waitForBidder,
            }

            let updatedProviders: any[]

            if (existingOptimeraIndex >= 0) {
                // Update existing provider
                updatedProviders = [...currentProviders]
                updatedProviders[existingOptimeraIndex] = optimeraProviderConfig
                logger('debug', 'Updating existing OptimeraRTD configuration')
            } else {
                // Add new provider
                updatedProviders = [...currentProviders, optimeraProviderConfig]
                logger('debug', 'Adding new OptimeraRTD configuration')
            }

            window.pbjs.setConfig({
                realTimeData: {
                    dataProviders: updatedProviders
                }
            })

            logger('debug', 'OptimeraRTD module configured successfully')
            document.dispatchEvent(new Event(IdentityEvent.OPTIMERA_READY))
        } catch (error) {
            logger('error', 'Failed to configure OptimeraRTD', error)
            document.dispatchEvent(new Event(Errors.OPTIMERA_ERROR))
        }
    }

    /**
     * Initialize optiCommon library configuration
     *
     * Set the configuration for using additional Optimera and custom
     * parameters.
     *
     * Optimera will by default include the following params:
     *   deviceType - mobile, desktop or tablet
     *   operatingSystem - ios, mac, windows, or linux
     *   browser - chrome, firefox, safari, or edge
     *   mobilePlatform - iPhone, iPad, or android
     *
     * Additional available parameters are 'browserVersion' and 'country'.
     * To also include you any of these you can pass them into the
     * additionalOptimeraParams array below, otherwise you can leave it empty.
     *
     * You may set any custom params to be used as well here. In the following
     * example, the configuration will also add 'loggedIn' and 'cookied' as
     * params to add to the data splitter. These params' values will be set
     * using setCustomParam() using your own logic.
     *
     * @private
     */
    private initializeOptiCommonLib(): void {
        if (!window.optiCommon) {
            logger('error', 'optiCommon is not available')
            return
        }

        try {
            window.optiCommon.init({
                splitters: {
                    additionalOptimeraParams: [],
                    customParams: ['loggedIn', 'userStatus'],
                    ignoreOptimeraDefaults: ['browserVersion']
                }
            })

            /**
             * Set the value for a custom param.
             * You will pass the param name and a string value.
             */
            const userState = this.getUserStateInfo()

            // Set custom parameters example
            window.optiCommon.setCustomParam({
                name: 'loggedIn',
                value: userState.loggedIn,
            })

            window.optiCommon.setCustomParam({
                name: 'userStatus',
                value: userState.status,
            })

            /**
             * Optionally override a pre-defined Optimera param value with your own
             * value.
             *
             * You may want to do this if you have your own mobile device detection
             * using optiCommon.overrideOptimeraParamValue
             *
             * The following example shows how to override the deviceType param.
             * The accepted values are 'de' 'mo' or 'tb'.
             *
             * optiCommon.overrideOptimeraParamValue = ('deviceType', 'mo')
             */

            // Apply splitter page-level targeting
            if (window.googletag?.pubads) {
                const pubads = window.googletag.pubads()
                if (pubads?.setTargeting) {
                    pubads.setTargeting('opti_page', window.optiCommon.getPageTargeting())
                    logger('debug', 'optiCommon initialized with splitter string: '
                        + window.optiCommon.getSplitterString())
                } else {
                    logger('error', 'googletag.pubads().setTargeting is not available')
                }
            } else {
                logger('error', 'googletag.pubads is not available')
            }
        } catch (error) {
            logger('error', 'Failed to initialize optiCommon', error)
        }
    }
}

export default Optimera
